import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  Suspense,
  useEffect,
  type Context,
  type ReactNode,
} from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { useLocation } from "wouter";
import { useUser } from "@/hooks/useUser";
import { parseLocaleFromPath } from "@/i18n/LocaleContext";
import { browserAuthReturnPath, resolveAuthReturnPath } from "@/lib/safeRedirect";
import {
  DRIVER_INVITATION_CONTINUATION_KEY,
  readDriverInvitationContinuation,
} from "@/lib/driverInvitationContinuation";

/**
 * Thin context/provider shell for the auth modal.
 *
 * The full auth modal UI (AuthModalRoot, ProfileCompletionDialog,
 * MfaChallengeOverlay, etc.) lives in AuthModal.tsx — 2400+ lines that
 * are only needed when the modal is open. We lazy-load that chunk via
 * `AuthModalInternals` so the entry bundle stays lean.
 *
 * Consumers that only need `useAuthModal` or `AuthModalProvider` should
 * import from this file. AuthModal.tsx re-exports both for backward
 * compatibility with files that import from there.
 */

const AuthModalInternals = lazyRetry(() =>
  import("@/components/auth/AuthModal").then((m) => ({
    default: m.AuthModalInternals,
  }))
);

export type AuthModalIntent = "signin" | "signup" | "auto";
export type AuthModalContextHint = "default" | "list-a-car" | "reserve-car";

export type Step =
  | "email"
  | "code"
  | "password"
  | "social-hint"
  | "signup"
  | "magic-sent"
  | "signup-confirm"
  | "verify-pending"
  | "soft-deleted";

export interface OpenOptions {
  intent?: AuthModalIntent;
  next?: string;
  prefilledEmail?: string;
  context?: AuthModalContextHint;
  initialStep?: Step;
}

export interface AuthModalContextValue {
  open: (opts?: OpenOptions) => void;
  close: () => void;
  isOpen: boolean;
}

/**
 * Recover a bounded invitation continuation left by an older client after a
 * successful full-page sign-out. Keep this fallback in the provider shell
 * instead of the lazy modal chunk so it remains available without opening the
 * auth UI. Current account switches navigate directly to login.
 */
export function DriverInvitationSignoutRecovery() {
  const { user, isLoading } = useUser();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (user || isLoading) return;
    const basePath = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "") || "/";
    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    const isLocaleRoot = parseLocaleFromPath(currentPath).innerPath === "/";
    if (currentPath !== "/" && currentPath !== basePath && !isLocaleRoot) return;

    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(DRIVER_INVITATION_CONTINUATION_KEY);
      if (stored) sessionStorage.removeItem(DRIVER_INVITATION_CONTINUATION_KEY);
    } catch {
      return;
    }
    const next = readDriverInvitationContinuation(stored);
    if (next) setLocation(`/login?next=${encodeURIComponent(next)}`);
  }, [isLoading, setLocation, user]);

  return null;
}

// HMR-safe context singleton. Without this cache every HMR cycle creates
// a brand new context object and consumers that weren't hot-reloaded in the
// same pass still hold the old reference — causing spurious "must be used
// within AuthModalProvider" errors. Stashing on globalThis keeps identity
// stable across HMR. In prod this is a normal createContext.
const AUTH_MODAL_CTX_KEY = "__bac_AuthModalContext__";
const g = globalThis as unknown as Record<string, unknown>;
export const AuthModalContext: Context<AuthModalContextValue | null> =
  (g[AUTH_MODAL_CTX_KEY] as
    | Context<AuthModalContextValue | null>
    | undefined) ??
  ((g[AUTH_MODAL_CTX_KEY] = createContext<AuthModalContextValue | null>(
    null,
  )) as Context<AuthModalContextValue | null>);

export function useAuthModal(): AuthModalContextValue {
  const ctx = useContext(AuthModalContext);
  if (!ctx)
    throw new Error("useAuthModal must be used within AuthModalProvider");
  return ctx;
}

export interface AuthModalInternalsProps {
  isOpen: boolean;
  onClose: () => void;
  intent: AuthModalIntent;
  next: string;
  prefilled: string;
  contextHint: AuthModalContextHint;
  initialStep?: Step;
  open: (opts?: OpenOptions) => void;
}

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [intent, setIntent] = useState<AuthModalIntent>("auto");
  const [next, setNext] = useState<string>("");
  const [prefilled, setPrefilled] = useState<string>("");
  const [contextHint, setContextHint] =
    useState<AuthModalContextHint>("default");
  const [initialStep, setInitialStep] = useState<Step | undefined>(undefined);

  const open = useCallback((opts?: OpenOptions) => {
    setIntent(opts?.intent ?? "auto");
    const current = typeof window === "undefined" ? "/" :
      browserAuthReturnPath(window.location, import.meta.env.BASE_URL);
    setNext(resolveAuthReturnPath(opts?.next, current));
    setPrefilled(opts?.prefilledEmail ?? "");
    setContextHint(opts?.context ?? "default");
    setInitialStep(opts?.initialStep);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<AuthModalContextValue>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen],
  );

  return (
    <AuthModalContext.Provider value={value}>
      <DriverInvitationSignoutRecovery />
      {children}
      {/* Lazy-load the heavy auth modal UI (forms, MFA overlays, profile
          completion dialog). The Suspense fallback is null because none of
          these components render visible UI until triggered by auth state or
          the user opening the modal. */}
      <Suspense fallback={null}>
        <AuthModalInternals
          isOpen={isOpen}
          onClose={close}
          intent={intent}
          next={next}
          prefilled={prefilled}
          contextHint={contextHint}
          initialStep={initialStep}
          open={open}
        />
      </Suspense>
    </AuthModalContext.Provider>
  );
}
